import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "../css/AppointmentCard.css";
import rahul from "../assets/rahul.png";
import api from "../api";

const typeLabels = {
  home_visit: "Home Visit",
  video_call: "Video Call",
  clinical_visit: "Clinical Visit",
};

const AppointmentCard = ({
  doctor,
  date,
  time,
  type,
  amount,
  status,
  assignedTo,
  appointmentData,
  onReject,
  onAccept,
}) => {
  const [selectedAction, setSelectedAction] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isRejected, setIsRejected] = useState(false); // ✅ new state
  const navigate = useNavigate();

  const handleCardClick = () => {
    if (!isDisabled) {
      navigate("/AppointmentDetailsScreenTwo", { state: { appointmentData } });
    }
  };

  const handleRejectClick = async (e) => {
    e.stopPropagation();
    setSelectedAction("reject");
    setIsDisabled(true);

    try {
      const token = localStorage.getItem("accessToken");
      const appointmentId = appointmentData?.id;
      if (!appointmentId) {
        alert("Missing appointment ID — cannot reject this item.");
        return;
      }

      const response = await api.delete("/admin/doctor-appointments/assign/", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: { appointment_id: appointmentId },
      });

      if (response.status === 200 || response.status === 204) {
        setIsRejected(true); // ✅ show reject message
        if (onReject) onReject(appointmentId);
      }
    } catch (error) {
      console.error("Error rejecting appointment:", error);
      alert("Failed to reject appointment. Please try again.");
    }
  };

  const handleAcceptClick = async (e) => {
    e.stopPropagation();
    setSelectedAction("accept");
    setShowModal(true);
    setLoading(true);

    try {
      const token = localStorage.getItem("accessToken");
      const response = await api.get("/admin/doctor-appointments/assign/", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response?.data) {
        const available = response.data.available_doctors.map((d) => ({
          id: d.id,
          name: d.name,
          email: d.email,
          available: true,
          phone: d.phone,
          specialization: d.specialization,
          consultation_type: d.consultation_type,
          avatar: d.profile_image
            ? `${api.defaults.baseURL.split("/api")[0]}${d.profile_image}`
            : rahul,
        }));

        const unavailable = response.data.unavailable_doctors.map((d) => ({
          id: d.id,
          name: d.name,
          email: d.email,
          available: false,
          phone: d.phone,
          specialization: d.specialization,
          consultation_type: d.consultation_type,
          avatar: d.profile_image
            ? `${api.defaults.baseURL.split("/api")[0]}${d.profile_image}`
            : rahul,
        }));

        setProviders([...available, ...unavailable]);
      }
    } catch (error) {
      console.error("Error fetching doctors:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (name, id) => {
    setShowModal(false);
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const payload = {
        appointment_id: appointmentData?.id,
        doctor_id: id,
      };

      const response = await api.post(
        "/admin/doctor-appointments/assign/",
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response?.data?.appointment) {
        const assignedDoctorName =
          response.data.appointment.doctor_assigned?.name || name;

        if (onAccept) onAccept(assignedDoctorName);
      }
    } catch (error) {
      console.error("Error assigning doctor:", error);
      alert("Failed to assign doctor. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => setShowModal(false);

  const getAssignedToText = (assignedTo) => {
    if (Array.isArray(assignedTo))
      return assignedTo.length ? assignedTo.join(", ") : "NA";
    return assignedTo?.trim() || "NA";
  };

  const showButtons = type === "home_visit" || type === "video_call";

  return (
    <>
      <motion.div
        className={`appointment-box ${isDisabled ? "disabled-card" : ""}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        whileHover={
          !isDisabled
            ? {
                scale: 1.05,
                y: -5,
                boxShadow: "0px 12px 24px rgba(0, 0, 0, 0.2)",
              }
            : {}
        }
        onClick={handleCardClick}
        style={{
          cursor: isDisabled ? "not-allowed" : "pointer",
          opacity: isDisabled ? 0.5 : 1,
        }}
      >
        {/* Profile Section */}
        <div className="appointment-profile">
          <img
            src={doctor.avatar || rahul}
            alt={doctor.name}
            className="appointment-image"
          />
          <div className="appointment-profile-content">
            <h3 className="appointment-name">{doctor.name}</h3>
            <p className="appointment-type">{typeLabels[type]}</p>
            <p className="appointment-time">
              {date}, {time}
            </p>
            <p className="appointment-text">
              Amount Paid: <span className="appointment-amount">{amount}</span>
            </p>
          </div>
        </div>

        {/* Footer Section */}
        <div className="appointment-footer">
          {/* ✅ show message if rejected */}
          {isRejected ? (
            <div className="appointment-rejected-box">
              <p className="appointment-rejected-text">
                This customer was rejected by Admin
              </p>
            </div>
          ) : (
            <>
              {status === "new" && showButtons && (
                <div className="appointment-buttons">
                  <button
                    onClick={handleRejectClick}
                    className="appointment-btn reject-btn"
                  >
                    Reject
                  </button>
                  <button
                    onClick={handleAcceptClick}
                    className="appointment-btn accept-btn"
                  >
                    Assign
                  </button>
                </div>
              )}

              {status === "assigned" && (
                <div>
                  <p className="appointment-status">
                    Assigned to: <strong>{getAssignedToText(assignedTo)}</strong>
                  </p>
                  <p className="appointment-status-assigned">
                    This Appointment is Assigned
                  </p>
                </div>
              )}

              {status === "ongoing" && (
                <div>
                  <p className="appointment-status-ongoing">
                    Assigned to: <strong>{getAssignedToText(assignedTo)}</strong>
                  </p>
                  <p className="appointment-status-ongoing-item">
                    This Appointment is Ongoing
                  </p>
                </div>
              )}

              {status === "completed" && (
                <div>
                  <p className="appointment-status-completed">
                    Assigned to: <strong>{getAssignedToText(assignedTo)}</strong>
                  </p>
                  <p className="appointment-status-completed-item">
                    This Appointment has been Completed
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>

      {/* Modal remains unchanged */}
      {showModal && (
        <div className="booking-modal-overlay" onClick={handleCloseModal}>
          <div
            className="booking-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="booking-modal-header">
              <h3 className="booking-model-title">Doctors Lists</h3>
              <button
                className="booking-modal-close"
                onClick={handleCloseModal}
              >
                Close
              </button>
            </div>

            {loading ? (
              <div style={{ textAlign: "center", padding: "1rem" }}>
                Loading providers...
              </div>
            ) : (
              <div className="booking-service-provider-list">
                {providers.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "1rem" }}>
                    No providers found
                  </div>
                ) : (
                  providers.map((p, i) => (
                    <div key={i} className="booking-service-provider-item">
                      <div className="booking-provider-info">
                        <img
                          src={p.avatar || rahul}
                          alt={p.name}
                          className="booking-provider-avatar"
                        />
                        <div className="booking-provider-details">
                          <span className="booking-provider-name">
                            {p.name}
                          </span>
                          <span className="booking-provider-type">
                            {p.email}
                          </span>
                          <span
                            className={`booking-provider-status ${
                              p.available
                                ? "booking-available"
                                : "booking-not-available"
                            }`}
                          >
                            {p.available ? "Available" : "Not Available"}
                          </span>
                        </div>
                      </div>
                      <button
                        disabled={!p.available}
                        className="booking-assign-button"
                        onClick={() => handleAssign(p.name, p.id)}
                      >
                        Assign
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AppointmentCard;
